const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/liho/eCheckin/sections/PersAngaben.tsx';
import * as React from 'react';
import { forwardRef } from 'react';
import { useIntl } from 'react-intl';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import { ECheckinSectionProps } from 'App/components/eCheckin/types';
import ECheckinSection from 'App/components/eCheckin/components/ECheckinSection';
import textMap from 'App/config/liho/eCheckin/translations';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import textMapCommon from '../../../common/translations';
import { CountryField } from 'App/components/eCheckin/components/fields/metakat/CountryField';
import { GenderField } from 'App/components/eCheckin/components/fields/metakat/GenderField';
import { ZivilstandField } from 'App/components/eCheckin/components/fields/metakat/ZivilstandField';
import { ReligionField } from 'App/components/eCheckin/components/fields/metakat/ReligionField';
import { AddressBlock, AddressFieldsConfig } from 'App/components/eCheckin/components/fields/AddressBlock';
import { EMailField } from 'App/components/eCheckin/components/fields/EMailField';
import { PhoneNumberField } from 'App/components/eCheckin/components/fields/PhoneNumberField';
import { createFieldPath } from 'App/components/eCheckin/utils';
import { AHVField } from 'App/components/eCheckin/components/fields/AHVField';
import { BirthDateField } from 'App/components/eCheckin/components/fields/BirthDateField';
import { YesNoField } from 'App/components/eCheckin/components/fields/YesNoField';
import { CIS4mBlock } from '@cistec/common_web/modules/CIS4m/components/CIS4mBlock';
import _ from 'lodash';
import { KorrespondenzspracheField } from 'App/components/eCheckin/components/fields/metakat/KorrespondenzspracheField';
import CIS4mSingleCheckbox from '@cistec/common_web/modules/CIS4m/components/CIS4mSingleCheckbox';
import { atLeastOneHasValue } from '@cistec/common_web/modules/CIS4m/utils/data';
import { useText } from '@cistec/common_web/utils/intlUtils';
import { selectSelectedECheckin } from 'App/components/eCheckin/store';
import { useSelector } from 'react-redux';
import { Divider } from '@mui/material';

export const PersAngaben = forwardRef((props: ECheckinSectionProps, ref: any) => {
    const { path } = props;
    const title = useText(props.title);
    const { fallNr, tKey } = useSelector(selectSelectedECheckin);

    const PATH_AHV = createFieldPath(path, 'ahv');
    const PATH_RECHNUNGSKOPIE = createFieldPath(path, 'rechnungskopie');
    const PATH_FIRST_NAME = createFieldPath(path, 'firstname');
    const PATH_LAST_NAME = createFieldPath(path, 'lastname');
    const PATH_LEDIGER_NAME = createFieldPath(path, 'maidenname');
    const PATH_GENDER = createFieldPath(path, 'gender');
    const PATH_DATE_OF_BIRTH = createFieldPath(path, 'dateOfBirth');
    const PATH_ZIVILSTAND = createFieldPath(path, 'zivilstand');
    const PATH_NATIONALITY = createFieldPath(path, 'nationality');
    const PATH_BERUF = createFieldPath(path, 'profession');
    const PATH_RELIGION = createFieldPath(path, 'religion');
    const PATH_COUNTRY = createFieldPath(path, 'country');
    const PATH_EMAIL = createFieldPath(path, 'email');
    const PATH_TEL = createFieldPath(path, 'tel');
    const PATH_TEL_MOBILE = createFieldPath(path, 'mobile');
    const PATH_TEL_OFFICE = createFieldPath(path, 'office');

    const PATH_ZIVILWOHNSITZ = createFieldPath(path, 'zivilWohnsitz');
    const PATH_ZIVILWOHNSITZ_PLZ = createFieldPath(path, 'zivilWohnPlz');
    const PATH_ZIVILWOHNSITZ_ORT = createFieldPath(path, 'zivilWohnOrt');
    const PATH_ZIVILWOHNSITZ_KANTON = createFieldPath(path, 'zivilKanton');

    const PATH_KORRESPONDENZADRESSE = createFieldPath(path, 'korrespondenzAdresse');
    const PATH_KORRESPONDENZADRESSE_FIRST_NAME = createFieldPath(path, 'korrespFirstname');
    const PATH_KORRESPONDENZADRESSE_LAST_NAME = createFieldPath(path, 'korrespLastname');
    const PATH_KORRESPONDENZADRESSE_COUNTRY = createFieldPath(path, 'korrespCountry');
    const PATH_KORRESPONDENZADRESSE_TEL = createFieldPath(path, 'korrespTel');
    const PATH_KORRESPONDENZADRESSE_TEL_MOBILE = createFieldPath(path, 'korrespMobile');
    const PATH_KORRESPONDENZADRESSE_TEL_OFFICE = createFieldPath(path, 'korrespOffice');

    const PATH_AUFGEBOT = createFieldPath(path, 'eAufgebot');
    const PATH_ELEKTRONISCHE_KOMMUNIKATION = createFieldPath(path, 'ePatientenportal');
    const PATH_KORRESPONDENZSPRACHE = createFieldPath(path, 'sprache');

    const { formatMessage } = useIntl();

    const addressFieldConfig: AddressFieldsConfig = {
        additionalInfo: {
            path: 'adressZusatz',
        },
        location: {
            maxLength: 30,
        },
    };

    const correspondenceAddressBlockConfig: AddressFieldsConfig = {
        streetAndNumber: {
            path: 'korrespStreetAndNumber',
        },
        additionalInfo: {
            path: 'korrespAdressZusatz',
        },
        postalCode: {
            path: 'korrespPlz',
        },
        location: {
            path: 'korrespOrt',
            maxLength: 30,
        },
        country: {
            path: 'korrespCountry',
        },
    };

    const formData = useCIS4mData();
    const phoneNumberRequired = !atLeastOneHasValue(formData, [PATH_TEL, PATH_TEL_MOBILE, PATH_TEL_OFFICE]);
    const hideZivilWohnsitz = _.get(formData, PATH_ZIVILWOHNSITZ) !== 'Yes';
    const hideKorrespondenzaddresse = _.get(formData, PATH_KORRESPONDENZADRESSE) !== 'Yes';
    const korrespondenzPhoneNumberRequired = !atLeastOneHasValue(formData, [
        PATH_KORRESPONDENZADRESSE_TEL,
        PATH_KORRESPONDENZADRESSE_TEL_MOBILE,
        PATH_KORRESPONDENZADRESSE_TEL_OFFICE,
    ]);

    const eCheckinSectionRef = useCIS4m(ref);

    return (
        <ECheckinSection ref={eCheckinSectionRef} path={path} title={title}>
            <h1>{title}</h1>
            <p>{`${formatMessage(textMap.fid)}: ${fallNr}`}</p>
            <AHVField label={textMap.sozialversicherungsnr} path={PATH_AHV} required={true} />
            <CIS4mSingleCheckbox label={textMap.rechnugskopie} path={PATH_RECHNUNGSKOPIE} />
            <CIS4mTextField label={textMapCommon.lastname} path={PATH_LAST_NAME} required={true} />
            <CIS4mTextField label={textMapCommon.firstname} path={PATH_FIRST_NAME} required={true} />
            <CIS4mTextField label={textMapCommon.maidenname} path={PATH_LEDIGER_NAME} />
            <GenderField label={textMap.gender} path={PATH_GENDER} required={true} />
            <BirthDateField label={textMap.dateOfBirth} path={PATH_DATE_OF_BIRTH} required={true} />
            <ZivilstandField label={textMap.zivilstand} path={PATH_ZIVILSTAND} required={true} />
            <CountryField label={textMap.nationality} path={PATH_NATIONALITY} required={true} />
            <CIS4mTextField label={textMap.profession} path={PATH_BERUF} maxLength={50} />
            <ReligionField label={textMap.religion} path={PATH_RELIGION} required={true} />
            <AddressBlock basePath={path} required={true} fieldsConfig={addressFieldConfig} />
            <PhoneNumberField
                label={textMapCommon.tel}
                path={PATH_TEL}
                countryCodePath={PATH_COUNTRY}
                required={phoneNumberRequired}
            />
            <PhoneNumberField
                label={textMapCommon.mobile}
                path={PATH_TEL_MOBILE}
                countryCodePath={PATH_COUNTRY}
                required={phoneNumberRequired}
            />
            <PhoneNumberField
                label={textMap.office}
                path={PATH_TEL_OFFICE}
                countryCodePath={PATH_COUNTRY}
                required={phoneNumberRequired}
            />
            <EMailField label={textMapCommon.email} path={PATH_EMAIL} />

            <YesNoField
                label={textMap.zivilWohnsitz}
                infoText={textMap.zivilWohnsitzHelpText}
                path={PATH_ZIVILWOHNSITZ}
                yesValue="Yes"
                noValue="No"
                required={true}
            />
            <CIS4mBlock hidden={hideZivilWohnsitz}>
                <Divider />
                <h4>{formatMessage(textMap.zivilWohnsitz)}</h4>
                <CIS4mTextField label={textMapCommon.plz} path={PATH_ZIVILWOHNSITZ_PLZ} required={true} />
                <CIS4mTextField
                    label={textMapCommon.city}
                    path={PATH_ZIVILWOHNSITZ_ORT}
                    required={true}
                    maxLength={30}
                />
                <CIS4mTextField label={textMap.kanton} path={PATH_ZIVILWOHNSITZ_KANTON} />
                <Divider />
            </CIS4mBlock>

            <YesNoField
                label={textMap.korres}
                path={PATH_KORRESPONDENZADRESSE}
                yesValue="Yes"
                noValue="No"
                required={true}
            />
            <CIS4mBlock hidden={hideKorrespondenzaddresse}>
                <Divider />
                <h4>{formatMessage(textMap.korresAdresse)}</h4>
                <CIS4mTextField
                    label={textMapCommon.lastname}
                    path={PATH_KORRESPONDENZADRESSE_LAST_NAME}
                    required={true}
                />
                <CIS4mTextField
                    label={textMapCommon.firstname}
                    path={PATH_KORRESPONDENZADRESSE_FIRST_NAME}
                    required={true}
                />
                <AddressBlock basePath={path} required={true} fieldsConfig={correspondenceAddressBlockConfig} />
                <PhoneNumberField
                    label={textMapCommon.tel}
                    path={PATH_KORRESPONDENZADRESSE_TEL}
                    countryCodePath={PATH_KORRESPONDENZADRESSE_COUNTRY}
                    required={korrespondenzPhoneNumberRequired}
                />
                <PhoneNumberField
                    label={textMapCommon.mobile}
                    path={PATH_KORRESPONDENZADRESSE_TEL_MOBILE}
                    countryCodePath={PATH_KORRESPONDENZADRESSE_COUNTRY}
                    required={korrespondenzPhoneNumberRequired}
                />
                <PhoneNumberField
                    label={textMapCommon.office}
                    path={PATH_KORRESPONDENZADRESSE_TEL_OFFICE}
                    countryCodePath={PATH_KORRESPONDENZADRESSE_COUNTRY}
                    required={korrespondenzPhoneNumberRequired}
                />
                <Divider />
            </CIS4mBlock>
            <YesNoField
                label={textMap.eAufgebot}
                infoText={textMap.eAufgebotHelpText}
                path={PATH_AUFGEBOT}
                yesValue="Yes"
                noValue="No"
            />
            <YesNoField
                label={textMap.ePatientenportal}
                infoText={textMap.ePatientenportalHelpText}
                path={PATH_ELEKTRONISCHE_KOMMUNIKATION}
                yesValue="Yes"
                noValue="No"
            />
            <KorrespondenzspracheField
                label={textMap.requestedLanguage}
                path={PATH_KORRESPONDENZSPRACHE}
                required={true}
            />
        </ECheckinSection>
    );
});

PersAngaben.displayName = 'PersAngaben';

export default PersAngaben;
